'use client'

import { CollectionContainer } from '@/components/common/collection/collection-container'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import Autoplay from 'embla-carousel-autoplay'
import Image from 'next/image'

interface FeatureCard {
  image: string
  title: string
  description: string
}

interface Props {
  title?: string
  items: FeatureCard[]
}

const AUTOPLAY = {
  delay: 3000,
  stopOnInteraction: false,
}

// Embla chỉ loop được khi tổng chiều rộng slide đủ dài so với viewport,
// desktop hiện ~4 card nên dưới 6 card thì nhân đôi để infinite vẫn chạy
const MIN_LOOP_SLIDES = 6

export const SeamlessMotionFeatureCards = ({ title, items }: Props) => {
  const slides = items.length < MIN_LOOP_SLIDES ? [...items, ...items] : items

  return (
    <CollectionContainer className='max-lg:py-5'>
      {title ? (
        <h2 className='mb-6 font-criteria text-xl font-medium uppercase text-neutral-900 lg:text-[28px] lg:leading-10'>
          {title}
        </h2>
      ) : null}

      {/* Slide tự chạy + infinite; desktop hiện 4 card + lộ card kế tiếp, có thêm prev/next */}
      <Carousel
        opts={{
          align: 'start',
          loop: true,
        }}
        plugins={[Autoplay(AUTOPLAY)]}
        className='w-full'
      >
        <CarouselContent className='-ml-5'>
          {slides.map((item, index) => (
            <CarouselItem
              key={`${item.title}-${index}`}
              className='basis-[62%] pl-5 lg:basis-[24.17%]'
            >
              <div className='relative aspect-[435/558] overflow-hidden rounded-2xl'>
                <Image
                  src={ImageHelper.getImageUrlCdn(item.image)}
                  alt={item.title}
                  fill
                  sizes='(min-width: 1024px) 24vw, 62vw'
                  className='object-cover'
                />
                <div className='absolute inset-x-0 bottom-0 flex h-[55%] flex-col justify-end gap-1 bg-gradient-to-b from-transparent to-black/70 px-2 py-3 text-white lg:h-[38%] lg:px-5 lg:py-6'>
                  <h3 className='font-criteria text-lg font-medium uppercase leading-tight lg:text-xl 2xl:text-2xl'>
                    {item.title}
                  </h3>
                  <p className='text-xs lg:text-base'>{item.description}</p>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselPrevious
          className='absolute -left-5 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-black p-3 text-white max-lg:hidden'
          variant='outline'
        />
        <CarouselNext
          className='absolute -right-5 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-black p-3 text-white max-lg:hidden'
          variant='outline'
        />
      </Carousel>
    </CollectionContainer>
  )
}
